import { MediaAgent } from '@smarterservices/media-agent';
import { Room, VideoPresets, ScreenSharePresets, supportsAV1, supportsVP9 } from 'livekit-client';
import { create } from 'zustand';
import config from '../config';
import CONSTANTS from '../config/constants';
import ApiClient from '../lib/ApiClient';

export const createAppStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    initialized: false,
    sessionSid: null,
    apiUrl: null,
    roomId: null,
    spToken: null,
    identity: null,
    websocketUrl: null,
    iam: null,
    iamDefaultParts: {
      partition: 'ss',
      service: 'sp',
      region: '',
      account: '',
    },
    enabledComponents: config.defaultComponentState,
    sessionDetailsTab: 'flags', // Default active tab in SessionDetails
    isSessionLogModalOpen: false, // Session log modal state
    isIncidentReportModalOpen: false, // Incident report modal state
    isCheckinRequiredModalOpen: false, // Check-in required modal state
    isDiagnosticsModalOpen: false, // Session diagnostics modal state
    isTrackDebugModalOpen: false, // Track debug modal state
    isRemoteControlModalOpen: false, // Remote control modal state
    mediaClient: null,
    ApiClient: null,
    room: null,
    isRoomConnected: false,
    openCheckinDrawer: false,
    unreadChatMessageCount: 0,
    participantSid: null,
    type: null,
    viewMode: 'focus', // valid options are focus or overview
    isMicrophoneShareActive: false,
    isCameraShareActive: false,
    isScreenShareActive: false,
    attachedToProctorRoomSid: null,
    user: {
      sid: null,
      firstName: null,
      lastName: null,
      email: null,
      username: null,
      type: null,
    },

    // Sound management state
    soundToPlay: null, // { name: string, options: object }
    allowSounds: true, // Whether sounds are enabled

    magnification: {
      enabled: false,
      level: 2,
      lensSize: 200,
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    /**
     * Updates magnification settings
     *
     * @param {Object} magnification - Magnification configuration
     * @param {boolean} magnification.enabled - Whether magnification is enabled
     * @param {number} magnification.level - Magnification level
     * @param {number} magnification.lensSize - Lens size in pixels
     */
    updateMagnification: (magnification) => {
      set({ magnification });
    },

    /**
     * Update enabled components
     *
     * @param {Object} components - Components to enable/update
     * @param {boolean|Object} [components.sessionDetails] - Session details panel configuration
     *   - If boolean: true to enable with defaults, false to disable
     *   - If object: { defaultTab?: string, tabs?: Array<string|Object> }
     *     - defaultTab: Initial tab to display (e.g., 'flags', 'checkin', 'chat')
     *     - tabs: Array of tab configurations
     *       - String: Tab ID (e.g., 'flags', 'checkin', 'chat', 'journey', 'info', 'computer', 'identity')
     *       - Object: { id: string, title: string, url?: string }
     * @param {boolean} [components.timeline] - Enable timeline component
     * @param {boolean} [components.sessionHeader] - Enable session header
     * @param {boolean} [components.chat] - Enable chat component
     * @param {boolean} [components.microphone] - Enable microphone controls
     * @param {boolean} [components.camera] - Enable camera controls
     * @param {boolean} [components.settings] - Enable settings panel
     *
     * @example
     * // Enable with boolean
     * updateEnabledComponents({ sessionDetails: true })
     *
     * @example
     * // Enable with default tab
     * updateEnabledComponents({
     *   sessionDetails: {
     *     defaultTab: 'checkin',
     *     tabs: ['flags', 'checkin', 'chat', 'journey']
     *   }
     * })
     *
     * @example
     * // Enable with custom tabs
     * updateEnabledComponents({
     *   sessionDetails: {
     *     defaultTab: 'custom',
     *     tabs: [
     *       'flags',
     *       'checkin',
     *       { id: 'custom', title: 'Custom Tab', url: '/custom-content' }
     *     ]
     *   }
     * })
     */
    updateEnabledComponents: (components) => {
      const timestamp = performance.now();

      set((state) => ({
        enabledComponents: {
          ...state.enabledComponents,
          ...components
        }
      }));

      get().setViewMode();
    },

    /**
     * Set the view mode for the session viewer
     *
     * Controls whether the session is displayed in 'overview' mode (compact view without
     * session details panel) or 'focus' mode (expanded view with session details panel).
     *
     * **Behavior:**
     * - If a viewMode is explicitly provided, it will be set directly
     * - If no viewMode is provided, it will be determined automatically based on whether
     *   the SessionDetails component is enabled:
     *   - SessionDetails disabled → 'overview' mode
     *   - SessionDetails enabled → 'focus' mode
     *
     * **View Modes:**
     * - `'overview'`: Compact view, typically used in grid layouts with multiple sessions
     * - `'focus'`: Expanded view with session details panel, typically used for single session focus
     *
     * @function setViewMode
     * @param {string} [viewMode] - The view mode to set ('overview' or 'focus').
     *                              If not provided, mode is determined by SessionDetails enabled state
     * @returns {void}
     *
     * @example
     * // Explicitly set to overview mode
     * AppStore.setViewMode('overview');
     *
     * @example
     * // Explicitly set to focus mode
     * AppStore.setViewMode('focus');
     *
     * @example
     * // Auto-determine mode based on SessionDetails enabled state
     * AppStore.setViewMode();
     * // If SessionDetails is enabled → 'focus' mode
     * // If SessionDetails is disabled → 'overview' mode
     */
    setViewMode: (viewMode) => {

      if (viewMode) {
        set({ viewMode });
        return;
      }
      // now if SessionDetails is not enabled, we will consider this in overview mode, if so we will consider it focused mode...
      const isSessionDetailsEnabled = get().enabledComponents.sessionDetails || false;
      if (!isSessionDetailsEnabled) {
        set({ viewMode: 'overview' });
      } else {
        set({ viewMode: 'focus' });
      }
    },

    /**
     * Set the active tab in SessionDetails
     * Accepts a string or array of tab identifiers and sets the first enabled tab (like SQL COALESCE)
     *
     * @param {string|string[]} tabOrTabs - Tab identifier or array of tab identifiers to try in order
     * @example
     * // Set a single tab (validates if enabled)
     * setSessionDetailsTab('flags')
     * 
     * @example
     * // Try multiple tabs in order, use first enabled one
     * setSessionDetailsTab(['flags', 'checkin', 'info'])
     */
    setSessionDetailsTab: (tabOrTabs) => {
      const sessionDetailsConfig = get().enabledComponents.sessionDetails;

      // If sessionDetails is not enabled, don't set any tab
      if (!sessionDetailsConfig) {
        return;
      }

      // Get the list of enabled tabs
      let enabledTabs;
      if (typeof sessionDetailsConfig === 'boolean') {
        // If boolean true, all default tabs are enabled
        enabledTabs = ['info', 'flags', 'identity', 'checkin', 'communication', 'journey', 'computer', 'incidentReport'];
      } else if (sessionDetailsConfig.tabs) {
        // If specific tabs are configured, use those
        enabledTabs = sessionDetailsConfig.tabs.map(tab =>
          typeof tab === 'string' ? tab : tab.id
        );
      } else {
        // Default tabs if no specific configuration
        enabledTabs = ['info', 'flags', 'identity', 'checkin', 'communication', 'journey', 'computer', 'incidentReport'];
      }

      // Add incident report tab if there is an incident report
      // This ensures the tab can be selected even if not in the static config
      const stores = get().stores;
      const incidentReport = stores?.examSessionStore?.getState?.()?.incidentReport;

      if (incidentReport && !enabledTabs.includes('incidentReport')) {
        enabledTabs.push('incidentReport');
      }

      // Convert single tab to array for consistent processing
      const tabsToTry = Array.isArray(tabOrTabs) ? tabOrTabs : [tabOrTabs];

      // Find the first enabled tab from the list
      const tabToSet = tabsToTry.find(tab => enabledTabs.includes(tab));

      // If a valid tab was found, set it. Otherwise, fall back to first enabled tab
      if (tabToSet) {
        set({ sessionDetailsTab: tabToSet });
      } else if (enabledTabs.length > 0) {
        // Fallback to first enabled tab if none of the requested tabs are enabled
        set({ sessionDetailsTab: enabledTabs[0] });
      }
    },

    /**
     * Set the view mode for the session
     * 
     * PR-3412: Allows imperative viewMode updates without remounting the component
     * Used by VTC to control audio rendering based on focus state
     *
     * @param {string} viewMode - The view mode ('focus' or 'overview')
     */
    setViewMode: (viewMode) => {
      set({ viewMode });
    },

    /**
     * Set the proctor room SID that this session is attached to
     *
     * @param {string} roomSid - The proctor room SID
     */
    setAttachedToProctorRoomSid: (roomSid) => {
      set({ attachedToProctorRoomSid: roomSid });
    },

    /**
     * Open the session log modal
     */
    openSessionLog: () => {
      set({ isSessionLogModalOpen: true });
    },

    /**
     * Close the session log modal
     */
    closeSessionLog: () => {
      set({ isSessionLogModalOpen: false });
    },

    /**
     * Set the session log modal open state
     *
     * @param {boolean} isOpen - Whether the modal should be open
     */
    setSessionLogModalOpen: (isOpen) => {
      set({ isSessionLogModalOpen: isOpen });
    },

    /**
     * Open the incident report modal
     */
    openIncidentReportModal: () => {
      set({ isIncidentReportModalOpen: true });
    },

    /**
     * Close the incident report modal
     */
    closeIncidentReportModal: () => {
      set({ isIncidentReportModalOpen: false });
    },

    /**
     * Set the incident report modal open state
     * @param {boolean} isOpen - Whether the modal should be open
     */
    setIncidentReportModalOpen: (isOpen) => {
      set({ isIncidentReportModalOpen: isOpen });
    },

    /**
     * Open the diagnostics modal
     */
    openDiagnosticsModal: () => {
      set({ isDiagnosticsModalOpen: true });
    },

    /**
     * Close the diagnostics modal
     */
    closeDiagnosticsModal: () => {
      set({ isDiagnosticsModalOpen: false });
    },

    /**
     * Set the diagnostics modal open state
     * @param {boolean} isOpen - Whether the modal should be open
     */
    setDiagnosticsModalOpen: (isOpen) => {
      set({ isDiagnosticsModalOpen: isOpen });
    },

    /**
     * Open the check-in required modal
     */
    openCheckinRequiredModal: () => {
      set({ isCheckinRequiredModalOpen: true });
    },

    /**
     * Close the check-in required modal
     */
    closeCheckinRequiredModal: () => {
      set({ isCheckinRequiredModalOpen: false });
    },

    /**
     * Set the check-in required modal open state
     * @param {boolean} isOpen - Whether the modal should be open
     */
    setCheckinRequiredModalOpen: (isOpen) => {
      set({ isCheckinRequiredModalOpen: isOpen });
    },

    /**
     * Open the track debug modal
     */
    openTrackDebugModal: () => {
      set({ isTrackDebugModalOpen: true });
    },

    /**
     * Close the track debug modal
     */
    closeTrackDebugModal: () => {
      set({ isTrackDebugModalOpen: false });
    },

    /**
     * Set the track debug modal open state
     * @param {boolean} isOpen - Whether the modal should be open
     */
    setTrackDebugModalOpen: (isOpen) => {
      set({ isTrackDebugModalOpen: isOpen });
    },

    /**
     * Open the remote control modal
     */
    openRemoteControlModal: () => {
      set({ isRemoteControlModalOpen: true });
    },

    /**
     * Close the remote control modal
     */
    closeRemoteControlModal: async () => {
      await get().stores.examSessionStore.getState().setRemoteControlId(null);
      set({ isRemoteControlModalOpen: false });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    /**
     * Initialize the app store and orchestrate the initialization of all dependent stores.
     *
     * This is the primary initialization function for the SessionViewer application. It handles
     * authentication, permission validation, media connection setup, and coordinates the parallel
     * initialization of all dependent stores (eventStore, examSessionStore, playerStore, and
     * communicationStore) to optimize startup performance.
     *
     * **Performance Optimization:**
     * The function uses parallel execution with Promise.allSettled() to significantly reduce
     * initialization time by running independent operations concurrently:
     * - **Parallel Group:** Media connection setup + eventStore init + examSessionStore init
     * - **Synchronous:** playerStore init (reads localStorage only)
     * - **Sequential:** communicationStore init (depends on mediaClient being available)
     *
     * This parallel approach reduces initialization time from sequential execution to concurrent,
     * resulting in approximately 5-6x faster initialization on typical network conditions.
     *
     * **Error Handling:**
     * Uses Promise.allSettled() to ensure that failures in individual operations don't block
     * the initialization of other components. Each failed operation is logged to the console
     * with specific error details for debugging.
     *
     * **Initialization Flow:**
     * 1. Set up ApiClient and initial state
     * 2. Validate authentication and retrieve permissions (sequential - required)
     * 3. Configure enabled components and player settings
     * 4. **Parallel Execution:** Build media connection (if needed) + initialize eventStore + initialize examSessionStore
     * 5. **Synchronous:** Initialize playerStore (reads localStorage)
     * 6. **Sequential:** Initialize communicationStore (depends on mediaClient)
     * 7. Set initialized flag to true
     *
     * @async
     * @function initialize
     * @param {Object} options - Initialization options
     * @param {string} options.spToken - Authentication token for API calls
     * @param {string} options.sessionSid - Session SID to initialize
     * @param {string} options.examSid - Exam SID associated with the session
     * @param {string} options.installSid - Installation SID for the session
     * @param {string} [options.type='instructor'] - User type (e.g., 'instructor', 'proctor')
     * @param {string} [options.viewMode='focus'] - Initial view mode ('focus' or 'overview')
     * @param {string} options.apiUrl - Base URL for API requests
     * @param {string} options.roomId - LiveKit room ID for media connections
     * @param {string} options.identity - User identity for LiveKit connections
     * @param {Object} [options.enabledComponents] - Component enable/disable configuration
     * @param {boolean|Object} [options.enabledComponents.sessionDetails] - Session details panel config
     * @param {boolean} [options.enabledComponents.timeline] - Enable timeline component
     * @param {boolean} [options.enabledComponents.sessionHeader] - Enable session header
     * @param {boolean} [options.enabledComponents.chat] - Enable chat component
     * @param {boolean} [options.enabledComponents.microphone] - Enable microphone controls
     * @param {boolean} [options.enabledComponents.camera] - Enable camera controls
     * @param {boolean} [options.enabledComponents.settings] - Enable settings panel
     * @param {Object} [options.video] - Video configuration
     * @param {string[]} [options.video.sources] - Available video sources ('live', 'recorded')
     * @param {string} [options.video.default] - Default video source
     * @param {Object} [options.magnification] - Magnification settings
     * @param {boolean} [options.magnification.enabled=false] - Enable magnification
     * @param {number} [options.magnification.level=2] - Magnification level
     * @param {number} [options.magnification.lensSize=200] - Lens size in pixels
     * @param {Object} props - Component props to pass to eventStore
     * @returns {Promise<void>}
     *
     * @throws {Error} Throws if authentication or permission validation fails
     *
     * @example
     * await appStore.getState().initialize({
     *   spToken: 'token_123',
     *   sessionSid: 'session_456',
     *   examSid: 'exam_789',
     *   installSid: 'install_abc',
     *   apiUrl: 'https://api.example.com',
     *   roomId: 'room_def',
     *   identity: 'user_ghi',
     *   type: 'instructor',
     *   viewMode: 'focus',
     *   enabledComponents: {
     *     sessionDetails: true,
     *     chat: true,
     *     microphone: true
     *   },
     *   video: {
     *     sources: ['live', 'recorded'],
     *     default: 'live'
     *   }
     * }, componentProps);
     *
     * @see {@link buildMediaConnection} For building LiveKit media connections
     * @see {@link createExamSessionStore} For exam session store initialization
     * @see {@link createPlayerStore} For player store initialization
     * @see {@link createCommunicationStore} For communication store initialization
     */
    initialize: async (options, props) => {
      set({
        ApiClient: new ApiClient({ spToken: options.spToken }),
        spToken: options.spToken,
        sessionSid: options.sessionSid,
        type: options.type || 'instructor', //TODO: need to validate this...
        viewMode: options.viewMode || 'focus',
      });

      // call check to ensure the token is valid...
      const check = await get().ApiClient.checkAuthentication();
      if (!check.successful) {
        // TODO: Flip state so an error page is shown..
        throw new Error('Failed to retrieve permissions');
      }


      // before we do anything, let's check the permissions...
      const permissions = await get().ApiClient.permissions();
      if (!permissions.successful) {
        // TODO: Flip state so an error page is shown..
        throw new Error('Failed to retrieve permissions');
      }

      const mergedEnabledComponents = {
        ...config.defaultComponentState,
        ...options.enabledComponents,
      };

      set({
        iam: permissions.data.iam,
        user: {
          firstName: check.data?.firstName,
          lastName: check.data?.lastName,
          email: check.data?.email,
          username: check.data?.username,
          type: options.type || 'instructor',
          sid: check.data?.proctorAccountUserSid,
        },
        enabledComponents: mergedEnabledComponents,
        magnification: {
          enabled: options?.magnification?.enabled ?? false,
          level: options?.magnification?.level ?? 2,
          lensSize: options?.magnification?.lensSize ?? 200,
        },
      });

      get().setViewMode();

      get()
        .stores.playerStore.getState()
        .setActiveVideoSource(options?.video?.default || config.video.default);
      get()
        .stores.playerStore.getState()
        .setCanHaveLiveVideo(
          options?.video?.sources?.includes(CONSTANTS.VIDEO_SOURCES.LIVE) ||
          false
        );
      get()
        .stores.playerStore.getState()
        .setCanHaveRecordedVideo(
          options?.video?.sources?.includes(CONSTANTS.VIDEO_SOURCES.RECORDED) ||
          false
        );

      set((state) => ({
        ...state,
        roomId: options.roomId,
        identity: options.identity,
        apiUrl: options.apiUrl,
        iamDefaultParts: {
          ...state.iamDefaultParts,
          account: options.installSid
        },
      }));

      // Initialize examSessionStore first
      await get().stores.examSessionStore.getState().initialize({
        sessionSid: options.sessionSid,
        examSid: options.examSid,
        installSid: options.installSid,
        isReconnect: options.isReconnect,
      });

      // Run media connection setup in parallel with eventStore initialization
      const parallelInitTasks = [
        // Initialize eventStore after examSessionStore is ready
        get().stores.eventStore.getState().initialize(props, {
          sessionSid: options.sessionSid,
          examSid: options.examSid,
          installSid: options.installSid,
        }),
      ];

      // Add media connection setup if needed
      if (get().stores.playerStore.getState()?.canHaveLiveVideo) {
        // Build media connection in parallel with store inits
        parallelInitTasks.push(get().buildMediaConnection());
      } else {
        // this session cannot have live video, meaning it is likely over...
        set({ apiUrl: options.apiUrl });
      }

      // Wait for all parallel tasks to complete
      const initResults = await Promise.allSettled(parallelInitTasks);

      // Log any failures
      const [eventStoreResult, mediaConnectionResult] = initResults;
      if (eventStoreResult.status === 'rejected') {
        console.error('Failed to initialize eventStore:', eventStoreResult.reason);
      }
      if (mediaConnectionResult && mediaConnectionResult.status === 'rejected') {
        console.error('Failed to build media connection:', mediaConnectionResult.reason);
      }

      // Initialize player store (synchronous - just reads localStorage)
      get().stores.playerStore.getState().initialize();

      // Setup communications store (depends on mediaClient being available)
      await get().stores.communicationStore.getState().initialize();

      // register the online/offline events...
      //await get().stores.eventStore.getState().initialize(props);

      // Signal that the app has been completely initialized...
      set({ initialized: true });
    },

    resetInitialization: () => {
      set({ initialized: false });
    },

    /**
     * Reinitialize the session viewer
     *
     * Resets the initialization state and reinitializes the app. If configs and props
     * are not provided, it will use the current state to rebuild them automatically.
     * This is useful for refreshing the session state after connection issues or when
     * the session needs to be reloaded.
     *
     * @param {Object} [configs] - Configuration options (same as initialize). If not provided, uses current state.
     * @param {Object} [props] - Component props (same as initialize). If not provided, uses current event props.
     * @param {string} [defaultSessionDetailsTab] - Default tab to show in SessionDetails. If not provided, uses current tab.
     * @param {string} [attachedToProctorRoomSid] - Proctor room SID to attach to. If not provided, uses current value.
     * @returns {Promise<void>}
     *
     * @example
     * // Reinitialize with explicit parameters
     * await AppStore.reinitialize(configs, props, 'flags', 'room_123');
     *
     * @example
     * // Reinitialize using current state (no parameters needed)
     * await AppStore.reinitialize();
     */
    reinitialize: async (configs, props, defaultSessionDetailsTab, attachedToProctorRoomSid) => {
      const currentState = get();

      // If configs not provided, build from current state
      if (!configs) {
        const examState = currentState.stores?.examSessionStore?.getState();
        const playerState = examState?.stores?.playerStore?.getState();

        configs = {
          sessionSid: currentState.sessionSid,
          spToken: currentState.spToken,
          apiUrl: currentState.apiUrl,
          roomId: currentState.roomId,
          identity: currentState.identity,
          websocketUrl: currentState.websocketUrl,
          type: currentState.type,
          viewMode: currentState.viewMode,
          enabledComponents: currentState.enabledComponents,
          video: {
            sources: playerState?.canHaveLiveVideo
              ? ['live', 'recorded']
              : ['recorded'],
            default: playerState?.activeVideoSource || 'recorded',
          },
          magnification: currentState.magnification,
          examSid: examState?.examSid,
          installSid: examState?.installSid,
        };
      }

      // If props not provided, get from EventStore
      if (!props) {
        props = currentState.stores?.eventStore?.getState()?.props || {};
      }

      // Use current values as defaults for optional parameters
      const tabToSet = defaultSessionDetailsTab || currentState.sessionDetailsTab;
      const roomSidToSet = attachedToProctorRoomSid || currentState.attachedToProctorRoomSid;

      // Reset initialization state if already initialized
      if (currentState.initialized) {
        get().resetInitialization();
      }

      // Reinitialize the application
      await get().initialize(configs, props);

      // Set the default SessionDetails tab
      if (tabToSet) {
        get().setSessionDetailsTab(tabToSet);
      }

      get().setViewMode();

      // Set the attached proctor room SID
      if (roomSidToSet) {
        get().setAttachedToProctorRoomSid(roomSidToSet);
      }
    },

    buildMediaConnection: async () => {

      // call to get the server information for this session....
      const mediaConnectionResponse = await get().ApiClient.getSessionMediaConnection({
        installSid: get().stores.examSessionStore.getState().installSid,
        examSid: get().stores.examSessionStore.getState().examSid,
        sessionSid: get().stores.examSessionStore.getState().sessionSid,
      });

      if (!mediaConnectionResponse.successful) {
        console.error('Failed to get media connection:', mediaConnectionResponse.error);
        return;
      }

      const websocketUrl = mediaConnectionResponse.data.websocketUrl;

      const clientConfig = {
        apiUrl: get().apiUrl,
        roomId: get().roomId,
        identity: get().identity,
        token: get().spToken,
        websocketUrl: websocketUrl,
        type: 'instructor',
      };

      const clientInstance = await MediaAgent(clientConfig);

      // Select best available codec: AV1 > VP9 > H264
      const preferCodec = supportsAV1() ? 'av1' : supportsVP9() ? 'vp9' : 'h264';

      const roomInstance = new Room({
        // Enable adaptive stream — adjusts video quality based on subscriber
        // viewport size. Critical for multi-session grids: small tiles receive
        // low-res layers while the focused session gets full quality.
        // IMPORTANT: pauseVideoInBackground must be false for proctoring.
        // The default (true) causes LiveKit to pause video tracks when the
        // browser tab is backgrounded or elements scroll out of the viewport
        // (via IntersectionObserver), leading to perceived freezes when the
        // proctor switches back.
        adaptiveStream: { pauseVideoInBackground: false },
        // Enable dynamic broadcasting — pauses unused simulcast layers on the
        // server when no subscriber needs them, saving server-side bandwidth.
        dynacast: true,
        publishDefaults: {
          simulcast: true,
          videoCodec: preferCodec,
          videoEncoding: VideoPresets.h720.encoding,
          videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
          screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
          screenShareSimulcastLayers: [VideoPresets.h720, VideoPresets.h360],
          scalabilityMode: 'L3T3_KEY',
          backupCodec: true,
        },
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
          frameRate: 30,
        },
        reconnectPolicy: {
          nextRetryDelayInMs: ({ retryCount }) => {
            const baseDelays = [500, 1000, 2000, 4000, 5000];
            const baseDelay = baseDelays[Math.min(retryCount, baseDelays.length - 1)];
            const jitter = baseDelay * (0.1 + Math.random() * 0.2);
            const delay = Math.floor(baseDelay + jitter);
            console.log(`Reconnection attempt ${retryCount}, next retry in ${delay}ms`);
            return delay;
          },
        },
      });

      const mediaClient = clientInstance;
      const room = roomInstance;
      clientInstance.setRoom(roomInstance);

      roomInstance.on('connected', (e) => {
        //console.log("Connected", e);
      });

      const DISCONNECT_REASONS = {
        0: 'UNKNOWN_REASON',
        1: 'CLIENT_INITIATED',
        2: 'DUPLICATE_IDENTITY',
        3: 'SERVER_SHUTDOWN',
        4: 'PARTICIPANT_REMOVED',
        5: 'ROOM_DELETED',
        6: 'STATE_MISMATCH',
        7: 'JOIN_FAILURE',
        8: 'MIGRATION',
        9: 'SIGNAL_CLOSE',
      }

      roomInstance.on('disconnected', (reason) => {
        console.log("Disconnected", DISCONNECT_REASONS[reason]);
      });

      set({
        mediaClient,
        room,
        roomId: get().roomId,
        identity: get().identity,
        apiUrl: get().apiUrl,
        websocketUrl: websocketUrl,
      });
    },


    /**
     * Set the isRoomConnected state to the given value.
     *
     * This is used to update the app store when the connection to the room is
     * established or lost.
     *
     * @param {boolean} value - The value to set isRoomConnected to.
     */
    setIsRoomConnected: (value) => {
      if (value) {
        // call to make sure all the events are properly setup as well...
        get().storeState('eventStore').setupRoomEvents();
      }

      set({ isRoomConnected: value });
    },

    /**
     * Set the unreadChatMessageCount state to the given value.
     *
     * This is used to update the app store when there are new unread messages
     * in the chat.
     *
     * @param {number} count - The number of unread messages.
     */
    setUnreadChatMessageCount: (count) => {
      set({ unreadChatMessageCount: count });
    },

    /**
     * Sets the microphone share active state and manages student-side alerts
     *
     * Controls whether the proctor's microphone is actively being shared with the student.
     * When enabled, displays a prominent warning alert in the student's exam interface.
     * When disabled, removes the alert.
     *
     * @function setIsMicrophoneShareActive
     * @param {boolean} value - Whether microphone sharing is active
     * @returns {void}
     *
     * @description
     * **Behavior:**
     * - Validates that value is a boolean type
     * - Skips update if value hasn't changed (prevents unnecessary re-renders)
     * - When enabled (true):
     *   - Invokes 'raiseToolbarAlert' action on student's exam session
     *   - Shows warning: "Connected: Live Voice Chat with Proctor"
     *   - Alert has highest priority (999) and is non-dismissible
     * - When disabled (false):
     *   - Invokes 'removeToolbarAlert' action on student's exam session
     *   - Removes the 'proctorMicrophoneEnabledAlert' from student's view
     *
     * **Alert Properties:**
     * - ID: 'proctorMicrophoneEnabledAlert'
     * - Level: 'warning' (yellow/orange visual indicator)
     * - Priority: 999 (highest priority, appears above other alerts)
     * - Dismissible: false (student cannot close the alert)
     *
     * @example
     * // Enable microphone sharing and show alert to student
     * appStore.getState().setIsMicrophoneShareActive(true);
     *
     * @example
     * // Disable microphone sharing and remove alert
     * appStore.getState().setIsMicrophoneShareActive(false);
     *
     * @note This is used in conjunction with the AudioConnection component
     * to notify students when live audio chat with the proctor is active.
     */
    setIsMicrophoneShareActive: async (value) => {

      // Ensure there is an actual change to prevent unnecessary updates
      if (value === get().isMicrophoneShareActive) {
        return;
      }

      // Validate input type
      if (typeof value !== 'boolean') {
        console.warn('[AppStore] Invalid microphone share value:', value);
        return;
      }

      // When enabling microphone share, show alert to student
      if (value) {
        await get()
          .stores.examSessionStore.getState()
          .invokeAction({
            action: 'raiseToolbarAlert',
            alert: {
              id: 'proctorMicrophoneEnabledAlert',
              level: 'warning',
              message: 'Connected: Live Voice Chat with Proctor',
              priority: 999,
              dismissible: false,
              dismissTimeout: 0
            }
          });
        // now mute annotations...
        const updateMutedResult = await get().ApiClient.updateMutedAnnotations({
          installSid: get().stores.examSessionStore.getState().installSid,
          examSid: get().stores.examSessionStore.getState().examSid,
          sessionSid: get().stores.examSessionStore.getState().sessionSid,
          rules: {
            flags: ['speech-detected', 'noise-detected']
          }
        });
      }
      // When disabling microphone share, remove alert from student
      else {
        await get()
          .stores.examSessionStore.getState()
          .invokeAction({
            action: 'removeToolbarAlert',
            alertId: 'proctorMicrophoneEnabledAlert'
          });

        // unmute annotations when disabling
        const updateMutedResult = await get().ApiClient.updateMutedAnnotations({
          installSid: get().stores.examSessionStore.getState().installSid,
          examSid: get().stores.examSessionStore.getState().examSid,
          sessionSid: get().stores.examSessionStore.getState().sessionSid,
          rules: {
            flags: []
          }
        });
      }

      set({ isMicrophoneShareActive: value });
    },

    setIsCameraShareActive: (value) => {
      set({ isCameraShareActive: value });
    },

    setIsScreenShareActive: (value) => {
      set({ isScreenShareActive: value });
    },

    /**
     * Sets the proctor room SID that this session is attached to
     *
     * Used to track which proctor room a session belongs to, particularly useful
     * for transfer operations and multi-room scenarios.
     *
     * @function setAttachedToProctorRoomSid
     * @param {string|null} roomSid - The proctor room SID, or null to clear
     * @example
     * // Set the attached room
     * appStore.setAttachedToProctorRoomSid('room_abc123');
     *
     * // Clear the attached room
     * appStore.setAttachedToProctorRoomSid(null);
     */
    setAttachedToProctorRoomSid: (roomSid) => {
      set({ attachedToProctorRoomSid: roomSid });
    },

    /**
     * Play a sound
     *
     * Triggers a sound to be played by the Sounds component.
     *
     * @function playSound
     * @param {string} name - Name of the sound to play
     * @param {Object} [options={}] - Sound options (volume, playbackRate, etc.)
     * @example
     * appStore.playSound('new-session');
     * appStore.playSound('audio-check', { volume: 0.8 });
     */
    playSound: (name, options = {}) => {
      set({ soundToPlay: { name, options } });
    },

    /**
     * Clear the sound trigger
     *
     * Clears the soundToPlay state after a sound has been played.
     *
     * @function clearSound
     */
    clearSound: () => {
      set({ soundToPlay: null });
    },

    /**
     * Stop a currently playing sound
     *
     * Triggers the stop action for a specific sound.
     *
     * @function stopSound
     * @param {string} name - Name of the sound to stop
     * @example
     * appStore.stopSound('audio-check');
     */
    stopSound: (name) => {
      set({ soundToPlay: { name, options: { action: 'stop' } } });
    },

    /**
     * Set volume of a currently playing sound
     *
     * Adjusts the volume of a playing sound in real-time.
     *
     * @function setSoundVolume
     * @param {string} name - Name of the sound to adjust
     * @param {number} volume - Volume level (0.0 to 1.0)
     * @example
     * appStore.setSoundVolume('audio-check', 0.8);
     */
    setSoundVolume: (name, volume) => {
      set({ soundToPlay: { name, options: { action: 'setVolume', volume } } });
    },

    /**
     * Set whether sounds are allowed
     *
     * @function setAllowSounds
     * @param {boolean} allow - Whether to allow sounds
     * @example
     * appStore.setAllowSounds(false); // Disable sounds
     */
    setAllowSounds: (allow) => {
      set({ allowSounds: allow });
    },

    getIamResourceString: (resource, overrides = {}) => {
      const defaultParts = get().iamDefaultParts;
      const finalPartition = overrides.partition || defaultParts.partition;
      const finalService = overrides.service || defaultParts.service;
      const finalRegion = overrides.region || defaultParts.region;
      const finalAccount = overrides.account || defaultParts.account;


      return `ssrn:${finalPartition}:${finalService}:${finalRegion}:${finalAccount}:${resource}`;
    },

  }));
